import { DateTime } from "luxon"
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import { useMediaQuery } from "usehooks-ts";
import { useEffect, useState } from "react";
import { DesktopDateRangePicker } from "./DesktopDateRangePicker";
import { MobileDateRangePicker } from "./MobileDateRangePicker";
import { Day } from "./CustomDateRangeDay/CustomDateRangeDay";
import { Box } from "@mui/material";

export type RangePositionModel = 'start' | 'end' 

interface DataRangePickerProps {
    startDate?: DateTime,
    endDate?: DateTime,
    onStartDateChange?: (value: DateTime) => void,
    onEndDateChange?: (value: DateTime) => void,
    onMonthDecrease?: (value: any) => void,
    onMonthIncrease?: (value: any) => void,
    period?: DateTime,
    loading?: boolean,
    slots?: any,
    slotProps?: any
}

export interface SubDateRangePickerProps extends DataRangePickerProps {
    sxCalendar?: SxProps<Theme>,
    startPeriod?: DateTime,
    endPeriod?: DateTime,
    onRangePositionChange: (value: RangePositionModel) => void
};

export const DataRangePicker = (props: DataRangePickerProps): JSX.Element => {
    const {
        loading = false,
        startDate,
        endDate,
        onStartDateChange,
        onEndDateChange
    } = props

    const [sxCalendar, setSxCalendar] = useState<SxProps<Theme>>({
        width: "100%",
        '&': {
            height: `auto`,
            maxHeight: `100%`,
        },
        "& .MuiDayCalendar-monthContainer": {
            position: 'static'
        },
        "& .MuiDayCalendar-weekDayLabel": {
            width: {
                xs: '40px',
                md: '46px',
            },
            fontSize: '0.8rem',
            color: '#000000de'
        },
        "& .MuiPickersDay-root": {
            width: {
                xs: '40px',
                md: '46px',
            },
            height: {
                xs: '40px',
                md: '46px',
            },
            fontSize: '0.8rem'
        },
        "& .MuiPickersDay-today": {
            border: '4px solid #42a5f5'
        },
        "& .MuiPickersDay-today:not(.Mui-selected)": {
            border: '4px solid #42a5f5'
        },
        "& .MuiPickersDay--ok": {
            backgroundColor: '#4caf50',
            color: '#ffffff',
        },
        "& .MuiPickersDay--ok:hover": {
            backgroundColor: '#648AAF',
            color: '#FFF'
        },
        "& .MuiPickersDay--past-day": {
            backgroundColor: '#d4d4d4',
            color: '#FFF'
        },

        "& .MuiPickersDay--error": {
            backgroundColor: '#ef5350',
            color: '#ffffff',
        },
        "& .MuiPickersDay--error:hover": {
            backgroundColor: '#648AAF',
            color: '#FFF'
        },
        "& .MuiPickersDay--selected": {
            backgroundColor: '#648aaf'
        },
    });

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
    const [period, setPeriod] = useState<DateTime>(props.period || DateTime.fromJSDate(new Date()));
    const [startPeriod, setStartPeriod] = useState<DateTime>(period);
    const [endPeriod, setEndPeriod] = useState<DateTime>(period.plus({ months: 1 }));
    const [rangePosition, setRangePosition] = useState<RangePositionModel>('start');
    const [hoveredDay, setHoveredDay] = useState<DateTime | null>(null);

    useEffect(() => {
        setStartPeriod(period);
        setEndPeriod(period.plus({ months: 1 }));
    }, [period])

    const handleMonthDecrease = (date: any) => {
        setPeriod(period.minus({ months: 1 }));
    }

    const handleMonthIncrease = (date: any) => {
        setPeriod(period.plus({ months: 1 }));
    }

    const handleRangePositionChange = (value: RangePositionModel) => {
        setRangePosition(value)
    }

    const handleDayClick = (state: any) => {
        if (rangePosition === "start") {
            if (onStartDateChange) {
                onStartDateChange(state.day)
            }

            setRangePosition('end')
        }

        if (rangePosition === "end") {
            if (onEndDateChange) {
                onEndDateChange(state.day)
            }

            setRangePosition('start')
        }
    }

    const additionalProps = {
        onMonthIncrease: handleMonthIncrease,
        onMonthDecrease: handleMonthDecrease,
        startPeriod: startPeriod,
        endPeriod: endPeriod,
        loading: loading,
        sxCalendar: sxCalendar,
        onRangePositionChange: handleRangePositionChange,
        slots: {
            calendarHeader: () => (null),
            day: Day, 
        },
        slotProps: {
            day: (ownerState: any) => {
                return ({
                    selectedDay: undefined,
                    startDate,
                    endDate,
                    hoveredDay,
                    onPointerEnter: () => setHoveredDay(ownerState.day),
                    onPointerLeave: () => setHoveredDay(null),
                    onClick: () => handleDayClick(ownerState)
                }) as any
            },
        }
    }

    const render = () => {
        if (isSmallScreen) {
            return (
                <MobileDateRangePicker {...props} {...additionalProps}></MobileDateRangePicker>
            )
        }
        return (
            <DesktopDateRangePicker {...props}  {...additionalProps}></DesktopDateRangePicker>
        )
    }

    return (
        <Box>
            {
                render()
            }
        </Box>
    )
}